/**
 * Session for the Owner console — the web equivalent of `core/cloud/owner_session.dart`.
 *
 * Auth0 signs the user in; resto-api decides whether that account may use the cloud API at all
 * (`CloudLoginPolicy`: super admins and outlet OWNERs only). Bootstrap is two calls:
 *   GET /admin/users/me  → account + roles per outlet
 *   GET /outlets         → the outlets the account can see
 * The chosen outlet is kept in localStorage, mirroring `OwnerSession.selectedOutletId`.
 */
import { useAuth0 } from '@auth0/auth0-react';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ClockLoader } from 'react-spinners';
import { useApi, ApiError, errorText } from '../hooks/useApi';
import { Me, Outlet } from '../types/owner';
import NotAnOwner from '../pages/auth/NotAnOwner';
import NoOutlets from '../pages/auth/NoOutlets';
import StartupError from '../pages/auth/StartupError';

const OUTLET_KEY = 'resto.outletId';

export type BootstrapStatus = 'idle' | 'loading' | 'ready' | 'denied' | 'error';

export interface UserContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    /** Signed in *and* allowed to use the cloud API. */
    isOwner: boolean;
    isSuperAdmin: boolean;
    me: Me | null;
    /** Display name / e-mail from Auth0, available before the backend answers. */
    accountName: string;
    outlets: Outlet[];
    outletId: string;
    outlet: Outlet | null;
    selectOutlet: (outletId: string) => void;
    /** Swaps in an outlet the page has just saved, so the shell does not have to bootstrap again. */
    replaceOutlet: (outlet: Outlet) => void;
    roles: string[];
    status: BootstrapStatus;
    reload: () => Promise<void>;
    login: () => void;
    signOut: () => void;
}

export const UserContext = createContext<UserContextValue | undefined>(undefined);

export const useUserContext = (): UserContextValue => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserContextProvider');
    }
    return context;
};

const BootstrapLoader = () => (
    <div className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-bg">
        <ClockLoader color="#2F4858" />
    </div>
);

export function UserContextProvider({ children }: { children: ReactNode }) {
    const api = useApi();
    const {
        isAuthenticated,
        isLoading,
        user,
        error: auth0Error,
        loginWithRedirect,
        logout,
    } = useAuth0();

    const [me, setMe] = useState<Me | null>(null);
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const [outletId, setOutletId] = useState<string>('');
    const [status, setStatus] = useState<BootstrapStatus>('idle');
    const [bootstrapError, setBootstrapError] = useState<string | null>(null);

    const bootstrap = useCallback(async () => {
        setStatus('loading');
        setBootstrapError(null);
        try {
            const account = await api.get<Me>('/admin/users/me');
            setMe(account);

            const all = (await api.get<Outlet[]>('/outlets')) ?? [];
            const visible = [...all].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
            setOutlets(visible);

            // Prefer an outlet the account actually owns, then the remembered one.
            const owned = Object.entries(account.outletRoles ?? {})
                .filter(([, roles]) => account.superAdmin || roles.includes('OWNER'))
                .map(([id]) => id);
            const remembered = localStorage.getItem(OUTLET_KEY) ?? '';
            const candidates = [remembered, ...owned, ...visible.map((o) => o.id)];
            const chosen = candidates.find((id) => id && visible.some((o) => o.id === id)) ?? '';
            setOutletId(chosen);
            setStatus('ready');
        } catch (e) {
            // 401/403 from resto-api means the account exists in Auth0 but may not use the cloud API.
            if (e instanceof ApiError && e.isForbidden) {
                setStatus('denied');
                setBootstrapError(e.message);
                return;
            }
            setBootstrapError(errorText(e));
            setStatus('error');
        }
    }, [api]);

    useEffect(() => {
        if (!isLoading && isAuthenticated && user) bootstrap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isAuthenticated, user]);

    useEffect(() => {
        if (outletId) localStorage.setItem(OUTLET_KEY, outletId);
    }, [outletId]);

    const selectOutlet = useCallback((id: string) => setOutletId(id), []);
    const replaceOutlet = useCallback(
        (saved: Outlet) => setOutlets((all) => all.map((o) => (o.id === saved.id ? { ...o, ...saved } : o))),
        [],
    );

    const login = useCallback(() => {
        loginWithRedirect();
    }, [loginWithRedirect]);

    const signOut = useCallback(() => {
        localStorage.removeItem(OUTLET_KEY);
        logout({ logoutParams: { returnTo: window.location.origin } });
    }, [logout]);

    const value = useMemo<UserContextValue>(() => {
        const roles = me?.outletRoles?.[outletId] ?? [];
        return {
            isAuthenticated,
            isLoading,
            isOwner: status === 'ready',
            isSuperAdmin: me?.superAdmin ?? false,
            me,
            accountName: me?.name || (user?.name as string) || (user?.email as string) || '',
            outlets,
            outletId,
            outlet: outlets.find((o) => o.id === outletId) ?? null,
            selectOutlet,
            replaceOutlet,
            roles,
            status,
            reload: bootstrap,
            login,
            signOut,
        };
    }, [isAuthenticated, isLoading, me, outlets, outletId, selectOutlet, replaceOutlet, status, bootstrap, login, signOut, user]);

    if (isLoading) return <BootstrapLoader />;
    if (auth0Error) return <StartupError message={auth0Error.message} onRetry={login} />;

    // Not signed in yet: App's DefaultRedirect starts the Auth0 flow.
    if (!isAuthenticated) return <UserContext.Provider value={value}>{children}</UserContext.Provider>;

    if (status === 'idle' || status === 'loading') return <BootstrapLoader />;
    if (status === 'denied') {
        return (
            <UserContext.Provider value={value}>
                <NotAnOwner message={bootstrapError} onSignOut={signOut} />
            </UserContext.Provider>
        );
    }
    if (status === 'error') {
        return (
            <UserContext.Provider value={value}>
                <StartupError message={bootstrapError} onRetry={bootstrap} onSignOut={signOut} />
            </UserContext.Provider>
        );
    }
    if (outlets.length === 0 || !outletId) {
        return (
            <UserContext.Provider value={value}>
                <NoOutlets onSignOut={signOut} />
            </UserContext.Provider>
        );
    }

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
